import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import getLang from 'I18n/utils/getLang';
import { KEY_PREFIX } from 'marketplace-ui-common/hooks/useSearchParams';
import { getIsPrerenderUserAgent } from 'marketplace-ui-common/utils/userAgent';
import { useUnifiedDetails } from 'marketplace-ui-details/hooks/useUnifiedDetails';
import { Redirect, generatePath, useLocation } from 'quartz-routing/react-router';
import { useMemo } from 'react';
import { StorefrontPageMetadata } from '../../components/StorefrontPageMetadata';
import { Paths } from '../../constants/paths';
import { Subscreens } from '../../constants/usage';
import { useAppContext } from '../../context/AppContext';
import { useFilterConfigData } from '../../filters/hooks/useFilterConfig';
import { useTrackProxyRedirect } from '../../hooks/useTrackProxyRedirect';
import { getFullPublicUrl } from '../../utils/urls';
import { useParsedSearchResultsPathParams } from '../hooks/useParsedSearchResultsPathParams';
import { jsx as _jsx } from "react/jsx-runtime";
function PrerenderLegacyListingRedirect({
  listingSlug
}) {
  const listingPath = generatePath(Paths.LISTING_DETAILS, {
    listingSlug
  });
  const appContext = useAppContext();
  const {
    unifiedDetailsLoading,
    unifiedDetailsError
  } = useUnifiedDetails({
    appContext,
    slug: listingSlug,
    language: getLang()
  });
  if (unifiedDetailsLoading) {
    return null;
  }
  if (!unifiedDetailsError) {
    return /*#__PURE__*/_jsx(StorefrontPageMetadata, {
      prerenderStatusCode: 301,
      prerenderHeaders: {
        Location: getFullPublicUrl(listingPath)
      }
    });
  }

  // Listing not found (or error): redirect to the listing page so
  // ListingDetails can render the appropriate 404/error state for Prerender.
  return /*#__PURE__*/_jsx(Redirect, {
    to: listingPath
  });
}
export function ProductsSearchResultsProxy({
  productType
}) {
  var _data$result, _data$result2;
  const parsedPathParams = useParsedSearchResultsPathParams();
  const location = useLocation();
  const {
    data,
    loading
  } = useFilterConfigData();
  const CATEGORY = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 || (_data$result = _data$result.filterConfigs) === null || _data$result === void 0 ? void 0 : _data$result.CATEGORY;
  const AGENT_TEAM = data === null || data === void 0 || (_data$result2 = data.result) === null || _data$result2 === void 0 || (_data$result2 = _data$result2.filterConfigs) === null || _data$result2 === void 0 ? void 0 : _data$result2.AGENT_TEAM;
  const {
    pathname,
    params,
    isLegacyListingRedirect,
    subscreen,
    filters,
    filterType,
    filterValue
  } = useMemo(() => {
    if (loading) {
      return {
        pathname: Paths.SEARCH_RESULTS,
        params: new URLSearchParams(),
        isLegacyListingRedirect: false,
        subscreen: '',
        filters: [],
        filterType: undefined,
        filterValue: undefined
      };
    }
    const urlParams = new URLSearchParams(location.search);
    let resolvedCategoryField;
    let resolvedCategoryValue;
    let resolvedTeamField;
    let resolvedTeamValue;
    if (productType === 'APP' && parsedPathParams.categorySlug) {
      var _CATEGORY$options$fin;
      const categoryEnumValue = CATEGORY === null || CATEGORY === void 0 || (_CATEGORY$options$fin = CATEGORY.options.find(o => o.slug === parsedPathParams.categorySlug)) === null || _CATEGORY$options$fin === void 0 ? void 0 : _CATEGORY$options$fin.value;
      if (categoryEnumValue && CATEGORY) {
        urlParams.set(`${KEY_PREFIX}${String(CATEGORY.field)}`, String(categoryEnumValue));
        resolvedCategoryField = String(CATEGORY.field);
        resolvedCategoryValue = String(categoryEnumValue);
      }

      // The legacy app marketplace used /apps/:slug for both categories and
      // individual app listings. If the slug doesn't match a category, assume
      // it's a listing slug and redirect to the listing details page.
      if (!categoryEnumValue) {
        return {
          pathname: generatePath(Paths.LISTING_DETAILS, {
            listingSlug: parsedPathParams.categorySlug
          }),
          params: new URLSearchParams(),
          isLegacyListingRedirect: true,
          subscreen: '',
          filters: [],
          filterType: undefined,
          filterValue: undefined
        };
      }
    }
    if (productType === 'AGENT' && parsedPathParams.teamSlug) {
      var _AGENT_TEAM$options$f;
      const teamEnumValue = AGENT_TEAM === null || AGENT_TEAM === void 0 || (_AGENT_TEAM$options$f = AGENT_TEAM.options.find(o => o.slug === parsedPathParams.teamSlug)) === null || _AGENT_TEAM$options$f === void 0 ? void 0 : _AGENT_TEAM$options$f.value;
      if (teamEnumValue && AGENT_TEAM) {
        urlParams.set(`${KEY_PREFIX}${String(AGENT_TEAM.field)}`, String(teamEnumValue));
        resolvedTeamField = String(AGENT_TEAM.field);
        resolvedTeamValue = String(teamEnumValue);
      }
    }
    urlParams.set(`${KEY_PREFIX}PRODUCT_TYPE`, productType);
    if (parsedPathParams.page) {
      urlParams.set(`${KEY_PREFIX}page`, parsedPathParams.page.toString());
    }
    const resolvedSubscreen = productType === 'APP' ? parsedPathParams.categorySlug ? Subscreens.PROXY_APP_CATEGORY : Subscreens.PROXY_ALL_APPS : parsedPathParams.teamSlug ? Subscreens.PROXY_AGENT_TEAM : Subscreens.PROXY_ALL_AGENTS;
    const primaryField = productType === 'APP' ? resolvedCategoryField : resolvedTeamField;
    const primaryValue = productType === 'APP' ? resolvedCategoryValue : resolvedTeamValue;
    return {
      pathname: Paths.SEARCH_RESULTS,
      params: urlParams,
      isLegacyListingRedirect: false,
      subscreen: resolvedSubscreen,
      filters: primaryField ? [primaryField] : [],
      filterType: primaryField,
      filterValue: primaryValue ? [primaryValue] : undefined
    };
  }, [AGENT_TEAM, CATEGORY, loading, location.search, parsedPathParams, productType]);
  useTrackProxyRedirect({
    filterType,
    filterValue,
    filters,
    loading,
    pageNumber: parsedPathParams.page,
    skip: isLegacyListingRedirect,
    subscreen,
    type: productType
  });
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      size: "medium",
      marginBlockStart: "1600"
    });
  }
  if (isLegacyListingRedirect && getIsPrerenderUserAgent()) {
    return /*#__PURE__*/_jsx(PrerenderLegacyListingRedirect, {
      listingSlug: parsedPathParams.categorySlug
    });
  }
  const search = params.toString();
  return /*#__PURE__*/_jsx(Redirect, {
    to: {
      pathname,
      search: search ? `?${search}` : ''
    }
  });
}